import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft,
  UserPlus,
  Trash2,
  Shield,
  ScanLine,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "usher",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOkMsg("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
      };
      const { data } = await api.post("/users", payload);
      setUsers((u) => [data, ...u]);
      setOkMsg(`Created ${data.role} account: ${data.email}`);
      setForm({ email: "", name: "", password: "", role: "usher" });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
    } catch (e2) {
      alert(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-users-page">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 mono-label mb-6 hover:text-[#002FA7]"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="mono-label text-[#666] mb-2">// ACCESS CONTROL</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-8">
          Users
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Create user form */}
          <section className="brut-card p-6 lg:col-span-1" data-testid="create-user-section">
            <div className="mono-label text-[#666] mb-2">// NEW ACCOUNT</div>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-5 flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Add User
            </h2>
            <form onSubmit={submit} className="space-y-4" data-testid="create-user-form">
              <div>
                <label htmlFor="name" className="mono-label block mb-2">
                  Full Name *
                </label>
                <input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="brut-input"
                  placeholder="Jane Doe"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <label htmlFor="email" className="mono-label block mb-2">
                  Email *
                </label>
                <input
                  id="email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="brut-input"
                  placeholder="jane@event.com"
                  autoComplete="off"
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <label htmlFor="password" className="mono-label block mb-2">
                  Password * (min 6 chars)
                </label>
                <input
                  id="password"
                  required
                  minLength={6}
                  type="text"
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  className="brut-input"
                  placeholder="••••••"
                  autoComplete="new-password"
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <label htmlFor="role" className="mono-label block mb-2">
                  Role *
                </label>
                <select
                  id="role"
                  value={form.role}
                  onChange={(e) => update("role", e.target.value)}
                  className="brut-input"
                  data-testid="user-role-select"
                >
                  <option value="usher">Usher (scan only)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>

              {err && (
                <div
                  className="border-2 border-[#FF3B30] bg-white p-3 flex items-start gap-2"
                  data-testid="user-form-error"
                >
                  <AlertTriangle className="w-4 h-4 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-medium">{err}</span>
                </div>
              )}
              {okMsg && (
                <div
                  className="border-2 border-[#00C853] bg-white p-3 flex items-start gap-2"
                  data-testid="user-form-success"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#00C853] mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-medium">{okMsg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="brut-btn-accent w-full flex items-center justify-center gap-2"
                data-testid="user-submit-btn"
              >
                {submitting ? (
                  <>
                    <div className="brut-spinner !w-5 !h-5" />
                    Creating…
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" /> Create User
                  </>
                )}
              </button>
            </form>
          </section>

          {/* Users list */}
          <section
            className="brut-card p-0 overflow-hidden lg:col-span-2"
            data-testid="users-list-section"
          >
            <div className="border-b-2 border-[#121212] p-4 sm:p-6 flex items-center justify-between">
              <div>
                <div className="mono-label text-[#666]">// ROSTER</div>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight">
                  All Users
                </h2>
              </div>
              <div className="mono-label text-[#666]">{users.length} TOTAL</div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12" data-testid="users-loading">
                <div className="brut-spinner" />
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F4F5] sticky top-0">
                    <tr className="border-b-2 border-[#121212]">
                      <th className="text-left px-4 py-3 mono-label">Role</th>
                      <th className="text-left px-4 py-3 mono-label">Name</th>
                      <th className="text-left px-4 py-3 mono-label">Email</th>
                      <th className="text-left px-4 py-3 mono-label hidden md:table-cell">
                        Created
                      </th>
                      <th className="text-right px-4 py-3 mono-label">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const isSelf = me && u.id === me.id;
                      return (
                        <tr
                          key={u.id}
                          className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5]"
                          data-testid={`user-row-${u.id}`}
                        >
                          <td className="px-4 py-3">
                            {u.role === "admin" ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#121212] text-white border-2 border-[#121212] mono-label">
                                <Shield className="w-3 h-3" /> Admin
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FFCC00] text-[#121212] border-2 border-[#121212] mono-label">
                                <ScanLine className="w-3 h-3" /> Usher
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-bold">
                            {u.name}
                            {isSelf && (
                              <span className="mono-label text-[#666] ml-2">
                                (you)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono break-all">{u.email}</td>
                          <td className="px-4 py-3 font-mono text-[#666] hidden md:table-cell">
                            {fmtDate(u.created_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleDelete(u)}
                              disabled={isSelf}
                              className="brut-btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1 hover:!bg-[#FF3B30] hover:!text-white disabled:opacity-30 disabled:hover:!bg-white disabled:hover:!text-[#121212]"
                              data-testid={`delete-user-${u.id}`}
                              title={isSelf ? "You cannot delete your own account" : "Delete user"}
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-[#666] py-10 font-mono">
                          No users yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
